import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@services/user.service';
import { BehaviorSubject, defer, forkJoin, Observable, of, Subject, throwError, timer } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CreateBatchSubscription } from '../graphql/subscriptions/on-create-batch';
import { DeleteBatchSubscription } from '../graphql/subscriptions/on-delete-batch';
import { UpdateBatchSubscription } from '../graphql/subscriptions/on-update-batch';
import { UpdateTerminalStatusSubscription } from '../graphql/subscriptions/on-update-terminalstatus';
import { IBatch, IBatchStatus, integerToBatchStatus, MesBatchService } from '../mes/mes-batch/mes-batch-service';
import { IConfiguration, IItem, IOrderPosition, ITerminalStatus, ITerminalStatusCellStateChanged, ITerminalStatusRowStateChanged, TerminalStatusState } from './models/production-plan.models';
import { ProductionPlanConfigurationService } from './production-plan-configuration.service';

@Injectable()
export class ProductionPlanService implements OnDestroy {
	private destroy$ = new Subject<void>();
	private configurationId$ = new BehaviorSubject<string>(null);
	private terminalIds: Set<string> = new Set();
	private batches = new Map<string, IBatch>();
	private subscriptionsStarted = false;
	public isLoading = false;

	public set configurationId(value: string) {
		this.configurationId$.next(value);
	}

	public get configurationId(): string {
		return this.configurationId$.value;
	}

	public configurationChanged$ = this.configurationId$.asObservable();

	private orderPositionAddSubject$ = new Subject<IOrderPosition>();
	private orderPositionRemoveSubject$ = new Subject<IOrderPosition>();
	private orderPositionsSubject$ = new BehaviorSubject<Map<string, IOrderPosition>>(new Map());
	private terminalStatuses = new Map<string, ITerminalStatus>();
	private terminalStatusesByOrderPosition = new Map<string, Map<string, ITerminalStatus>>();

	public orderPositionAdd$ = this.orderPositionAddSubject$.asObservable();
	public orderPositionRemove$ = this.orderPositionRemoveSubject$.asObservable();

	public orderPositions$ = this.orderPositionsSubject$.asObservable();
	private batchesSubject$ = new BehaviorSubject<IBatch[]>([]);
	public batches$ = this.batchesSubject$.asObservable();
	public terminalStatusChanged$ = new Subject<ITerminalStatus>();
	public terminalStatusCellStateChanged$ = new Subject<ITerminalStatusCellStateChanged>();
	public terminalStatusRowStateChanged$ = new Subject<ITerminalStatusRowStateChanged>();
	public terminalStatusRowsLoaded$ = new Subject<Set<string>>();

	constructor(
		private http: HttpClient,
		private userService: UserService,
		private mesBatchService: MesBatchService,
		private configurationService: ProductionPlanConfigurationService,
		private createBatchSubscription: CreateBatchSubscription,
		private updateBatchSubscription: UpdateBatchSubscription,
		private deleteBatchSubscription: DeleteBatchSubscription,
		private updateTerminalStatusSubscription: UpdateTerminalStatusSubscription,
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	getOrderPositionKey(orderNumber: number, position: number): string {
		return `${orderNumber}-${position}`;
	}

	getTerminalStatusKey(orderNumber: number, position: number, terminalId: string): string {
		return `${orderNumber}-${position}-${terminalId}`;
	}

	private parseData(raw: unknown): Record<string, unknown> {
		return raw ? JSON.parse(raw as string) : {};
	}

	// Load configuration - returns Observable, called once on init
	loadConfiguration(): Observable<IConfiguration> {
		return this.configurationService.getById(this.configurationId).pipe(
			tap((config) => this.setTerminalIds(config)),
			catchError((err) => {
				console.error('Failed to load configuration:', err);
				return throwError(() => err);
			}),
		);
	}

	private setTerminalIds(config: IConfiguration): void {
		this.configurationId = config.id;
		this.terminalIds.clear();
		config.statusColumns.forEach((col) => {
			col.terminalIds.forEach((id) => this.terminalIds.add(id));
		});
	}

	private resetData(): void {
		this.orderPositionsSubject$.next(new Map());
		this.terminalStatuses.clear();
		this.terminalStatusesByOrderPosition.clear();
		this.batches.clear();
		this.batchesSubject$.next([]);
	}

	private setBatches(batches: IBatch[]): void {
		this.batches = new Map(batches.filter((batch) => !!batch.id).map((batch) => [batch.id, batch]));
		this.emitBatches();
	}

	private emitBatches(): void {
		this.batchesSubject$.next(Array.from(this.batches.values()));
	}

	private isProductionBatch(batch: Pick<IBatch, 'id' | 'status'>): batch is IBatch & { id: string } {
		return !!batch.id && batch.status === IBatchStatus.Production;
	}

	private applyLoadedTerminalStatuses(statuses: ITerminalStatus[]): void {
		const rowKeys = new Set<string>();

		statuses.forEach((status) => {
			this.setTerminalStatus(status);
			rowKeys.add(this.getOrderPositionKey(status.orderNumber, status.position));
		});

		if (rowKeys.size > 0) {
			this.terminalStatusRowsLoaded$.next(rowKeys);
		}
	}

	private applyRealtimeTerminalStatus(status: ITerminalStatus): void {
		const previousRowStatuses = this.getTerminalStatusesForOrderPosition(status.orderNumber, status.position);
		const previousRowStatus = this.computeCellStatus(previousRowStatuses);
		const previousStatus = this.setTerminalStatus(status);

		this.terminalStatusChanged$.next(status);

		if (previousStatus && previousStatus !== status.status) {
			this.terminalStatusCellStateChanged$.next({
				status,
				previousStatus,
				currentStatus: status.status,
			});
		}

		const currentRowStatuses = this.getTerminalStatusesForOrderPosition(status.orderNumber, status.position);
		const currentRowStatus = this.computeCellStatus(currentRowStatuses);
		if (previousRowStatus !== currentRowStatus) {
			this.terminalStatusRowStateChanged$.next({
				orderNumber: status.orderNumber,
				position: status.position,
				terminalStatuses: currentRowStatuses,
				previousStatus: previousRowStatus,
				currentStatus: currentRowStatus,
			});
		}
	}

	private setTerminalStatus(status: ITerminalStatus): TerminalStatusState | undefined {
		const key = this.getTerminalStatusKey(status.orderNumber, status.position, status.terminalId);
		const previousStatus = this.terminalStatuses.get(key)?.status;
		this.terminalStatuses.set(key, status);

		const orderPositionKey = this.getOrderPositionKey(status.orderNumber, status.position);
		const rowStatuses = this.terminalStatusesByOrderPosition.get(orderPositionKey) ?? new Map<string, ITerminalStatus>();
		rowStatuses.set(status.terminalId, status);
		this.terminalStatusesByOrderPosition.set(orderPositionKey, rowStatuses);

		return previousStatus;
	}

	loadOrderPositions(batchId?: string): Observable<void> {
		const url = `${environment.awsEnvUri}/reporting/productionplans/${this.configurationId}/orderpositions${batchId ? `?batchId=${batchId}` : ''}`;
		return this.http.get<IOrderPosition[]>(url, { headers: this.userService.getHeaders() }).pipe(
			tap((positions) => {
				const currentMap = this.orderPositionsSubject$.getValue();
				positions.forEach((pos) => {
					pos.data = this.parseData(pos.data);
					const key = this.getOrderPositionKey(pos.orderNumber, pos.position);
					currentMap.set(key, pos);
				});
				this.orderPositionsSubject$.next(new Map(currentMap));
			}),
			map(() => undefined),
		);
	}

	private loadOrderPositionsWithRetry(batchId: string, retriesLeft = 5): Observable<void> {
		const url = `${environment.awsEnvUri}/reporting/productionplans/${this.configurationId}/orderpositions?batchId=${batchId}`;
		return this.http.get<IOrderPosition[]>(url, { headers: this.userService.getHeaders() }).pipe(
			switchMap((positions) => {
				if (positions.length > 0 || retriesLeft === 0) {
					const currentMap = this.orderPositionsSubject$.getValue();
					positions.forEach((pos) => {
						pos.data = this.parseData(pos.data);
						const key = this.getOrderPositionKey(pos.orderNumber, pos.position);
						currentMap.set(key, pos);
						this.orderPositionAddSubject$.next(pos);
					});
					return of(undefined);
				}

				return timer(2000).pipe(switchMap(() => this.loadOrderPositionsWithRetry(batchId, retriesLeft - 1)));
			}),
		);
	}

	loadTerminalStatuses(batchId?: string): Observable<void> {
		const loadPage = (nextToken?: string): Observable<void> => {
			const query = [`configurationId=${this.configurationId}`, batchId ? `batchId=${batchId}` : undefined, nextToken ? `nextToken=${encodeURIComponent(nextToken)}` : undefined, 'limit=250'].filter(Boolean).join('&');

			return this.http.get<{ items: ITerminalStatus[]; nextToken?: string | null }>(`${environment.awsEnvUri}/reporting/productionplans/terminalstatuses?${query}`, { headers: this.userService.getHeaders() }).pipe(
				tap((response) => this.applyLoadedTerminalStatuses(response.items)),
				switchMap((response) => (response.nextToken ? loadPage(response.nextToken) : of(undefined))),
			);
		};

		return loadPage();
	}

	loadInitialData(): Observable<void> {
		return defer(() => {
			this.isLoading = true;
			return this.mesBatchService.getAll().pipe(
				tap(() => this.resetData()),
				map((response) => response.result.filter((batch) => this.isProductionBatch(batch))),
				tap((batches) => this.setBatches(batches)),
				switchMap((batches) => {
					if (!batches.length) {
						return of(undefined);
					}
					return forkJoin(batches.flatMap((batch) => [this.loadOrderPositions(batch.id), this.loadTerminalStatuses(batch.id)])).pipe(map(() => undefined));
				}),
				finalize(() => {
					this.startSubscriptions();
					this.isLoading = false;
				}),
			);
		});
	}

	startSubscriptions(): void {
		if (this.subscriptionsStarted) {
			return;
		}
		this.subscriptionsStarted = true;
		this.setupCreateBatchSubscription();
		this.setupBatchSubscription();
		this.setupDeleteBatchSubscription();
		this.setupTerminalStatusSubscription();
	}

	private setupCreateBatchSubscription(): void {
		this.createBatchSubscription
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ onCreateBatch }) => {
					this.batches.set(onCreateBatch.id, onCreateBatch as IBatch);
					this.emitBatches();
				},
				error: (err) => console.error('Create batch subscription error:', err),
			});
	}

	private setupBatchSubscription(): void {
		this.updateBatchSubscription
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					const batch = data.onUpdateBatch;
					const isProduction = integerToBatchStatus(batch.status) === IBatchStatus.Production;
					this.batches.set(batch.id, batch as IBatch);
					if (isProduction) {
						this.emitBatches();
						forkJoin([this.loadOrderPositionsWithRetry(batch.id), this.loadTerminalStatuses(batch.id)]).subscribe({
							error: (err) => console.error('Failed to load batch data:', err),
						});
					} else {
						this.removeDataForBatch(batch.id);
						this.batches.delete(batch.id);
						this.emitBatches();
					}
				},
				error: (err) => console.error('Batch subscription error:', err),
			});
	}

	private setupDeleteBatchSubscription(): void {
		this.deleteBatchSubscription
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ onDeleteBatch }) => {
					this.removeDataForBatch(onDeleteBatch.id);
					this.batches.delete(onDeleteBatch.id);
					this.emitBatches();
				},
				error: (err) => console.error('Delete batch subscription error:', err),
			});
	}

	private setupTerminalStatusSubscription(): void {
		this.updateTerminalStatusSubscription
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					const status = data.onUpdateTerminalStatus;
					const unpackedStatus: ITerminalStatus = {
						...status,
						itemOperations: status.itemOperations.map((op: { id: string; alias: string; items: string }) => ({
							id: op.id,
							alias: op.alias,
							items: this.unpackItemOperationItems(op.items),
						})),
					};
					if (!this.terminalIds.has(unpackedStatus.terminalId)) return;
					this.applyRealtimeTerminalStatus(unpackedStatus);
				},
				error: (err) => console.error('TerminalStatus subscription error:', err),
			});
	}

	private unpackItemOperationItems(items: string): IItem[] {
		if (!items) return [];
		const bytes = atob(items);
		return Array.from(bytes, (flags) => ({
			reordered: (flags.charCodeAt(0) & 1) !== 0,
			completed: (flags.charCodeAt(0) & 2) !== 0,
			ready: (flags.charCodeAt(0) & 4) !== 0,
		}));
	}

	private removeDataForBatch(batchId: string): void {
		const positions = this.orderPositionsSubject$.getValue();

		for (const [key, pos] of positions) {
			if (pos.batchId === batchId) {
				positions.delete(key);
				this.orderPositionRemoveSubject$.next(pos);
			}
		}

		for (const [key, status] of this.terminalStatuses) {
			if (status.batchId !== batchId) {
				continue;
			}

			this.terminalStatuses.delete(key);
			const orderPositionKey = this.getOrderPositionKey(status.orderNumber, status.position);
			const rowStatuses = this.terminalStatusesByOrderPosition.get(orderPositionKey);
			if (!rowStatuses) {
				continue;
			}

			rowStatuses.delete(status.terminalId);
			if (rowStatuses.size === 0) {
				this.terminalStatusesByOrderPosition.delete(orderPositionKey);
			}
		}
	}

	getTerminalStatusesForCell(orderNumber: number, position: number, terminalIds: string[]): ITerminalStatus[] {
		return terminalIds.map((tid) => this.terminalStatuses.get(this.getTerminalStatusKey(orderNumber, position, tid))).filter((s): s is ITerminalStatus => s !== undefined);
	}

	getTerminalStatusesForOrderPosition(orderNumber: number, position: number): ITerminalStatus[] {
		const rowStatuses = this.terminalStatusesByOrderPosition.get(this.getOrderPositionKey(orderNumber, position));
		return rowStatuses ? Array.from(rowStatuses.values()) : [];
	}

	computeCellStatus(terminalStatuses: ITerminalStatus[]): TerminalStatusState {
		const relevant = terminalStatuses.filter((ts) => ts.status !== 'irrelevant');
		if (relevant.length === 0) return TerminalStatusState.Irrelevant;
		const statuses = relevant.map((ts) => ts.status.toLowerCase());
		if (statuses.every((s) => s === 'completed')) return TerminalStatusState.Completed;
		if (statuses.some((s) => s === 'inprogress' || s === 'completed')) return TerminalStatusState.InProgress;
		if (relevant.some((ts) => ts.itemOperations.some((op) => op.items.some((item) => item.ready)))) return TerminalStatusState.Ready;
		return TerminalStatusState.Pending;
	}

	getBatchName(batchId: string): string {
		const batch = this.batches.get(batchId);
		if (!batch) return batchId;
		return `(${batch.alias}) ${batch.name}`;
	}
}
